import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import NotFoundPage from "components/ResponsePages/404NotFoundPage";
import LoadingSkeleton from "ui/LoadingSkeleton";
import SurfaceHost from "ui/SurfaceHost";
import LegacySurfaceBridge from "ui/LegacySurfaceBridge";
import { useHideAppBarOnMobile } from "ui/AppChromeContext";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { getStoryUiStrings, resolveStoryLocale } from "i18n/storyUi";
import type { SurfaceContract } from "types/surfaces";

function parsePositiveUserId(value: string | undefined): number | null {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Legacy SPA route for `/stories/:userId`.
 *
 * With ADR 011 runway in place this is a thin orchestration shell:
 *
 *  - `<SurfaceHost mode="fullscreen">` owns the overlay chrome, body
 *    scroll lock, and Escape handling.
 *  - `<LegacySurfaceBridge surfaceKind="stories" mode="iframe">` owns
 *    the same-origin Next embed URL, `?embed=1` query, postMessage
 *    listener, and typed event dispatch.
 *
 * When `stories_v2` is off (rollback / incident containment) we render
 * the "temporarily unavailable" screen instead of falling back to a
 * legacy viewer — the CRA viewer was deleted with the stories v2 ship
 * and cannot be revived in a single PR.
 */
const UserStoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams<{ userId: string }>();
  const normalizedUserId = parsePositiveUserId(userId);
  const { isEnabled: storiesV2Enabled, isLoading: flagLoading } = useFeatureFlag(
    "stories_v2",
    true
  );
  const [hostOpen, setHostOpen] = useState<boolean>(true);
  const storyUi = useMemo(() => getStoryUiStrings(resolveStoryLocale()), []);
  useHideAppBarOnMobile();

  const dismissOverlay = useCallback(() => {
    setHostOpen(false);
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/");
  }, [navigate]);

  const contract = useMemo<SurfaceContract<null> | null>(() => {
    if (!normalizedUserId) return null;
    return {
      surfaceKind: "stories",
      entityType: "user",
      entityId: normalizedUserId,
      presentation: "fullscreen",
      initial: null,
      origin: {
        route: `${location.pathname}${location.search}`,
        scrollY: typeof window !== "undefined" ? window.scrollY : 0,
      },
    };
  }, [location.pathname, location.search, normalizedUserId]);

  useEffect(() => {
    if (!storiesV2Enabled) return;
    setHostOpen(true);
  }, [storiesV2Enabled]);

  if (!normalizedUserId) {
    return <NotFoundPage />;
  }

  if (flagLoading) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black text-white">
        <LoadingSkeleton variant="entity-card" count={1} />
      </div>
    );
  }

  if (!storiesV2Enabled) {
    return (
      <div className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-4 bg-black px-6 text-center text-white">
        <h1 className="text-lg font-semibold">{storyUi.storyPlaybackTitle}</h1>
        <p className="max-w-md text-sm text-white/80">
          {storyUi.storiesTemporarilyUnavailable}
        </p>
        <button
          type="button"
          onClick={dismissOverlay}
          className="rounded-full bg-white/15 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white/25"
        >
          {storyUi.backHome}
        </button>
      </div>
    );
  }

  if (!contract) {
    return <NotFoundPage />;
  }

  return (
    <SurfaceHost
      open={hostOpen}
      onOpenChange={(open) => {
        if (!open) {
          dismissOverlay();
        }
      }}
      presentation="fullscreen"
      title={storyUi.storyPlaybackTitle}
      testId="stories-v2-overlay"
    >
      {() => (
        <LegacySurfaceBridge
          {...contract}
          mode="iframe"
          title={storyUi.storyPlaybackTitle}
          onClose={dismissOverlay}
        />
      )}
    </SurfaceHost>
  );
};

export default UserStoriesPage;
